import React from 'react'
import styled from 'styled-components'
import {
  Root,
  Trigger,
  Portal,
  Content,
  Item as MenuItem,
  type DropdownMenuProps,
  type DropdownMenuContentProps
} from '@radix-ui/react-dropdown-menu'
import { Box, type BoxProps, Button, type ButtonProps, Text, type TextProps } from 'rebass/styled-components'

import theme from '../../theme'

import styles from './Menu.module.css'

const MENU_LIST_PROPS = {
  sm: {
    min: '200px',
    max: '350px'
  },
  md: {
    min: '290px',
    max: '305px'
  },
  lg: {
    min: '500px',
    max: '500px'
  }
}

export const Menu = ({ children, ...props }: DropdownMenuProps): JSX.Element => (
  <Root modal={false} {...props}>
    {children}
  </Root>
)

export const MenuTrigger = ({
  variant = 'buttons.primaryNavTrigger',
  children,
  sx,
  ...props
}: BoxProps): JSX.Element => (
  <Trigger asChild>
    <Box as="button" variant={variant} sx={{ ...sx }} {...props}>
      {children}
    </Box>
  </Trigger>
)

export interface MenuPopoverProps extends DropdownMenuContentProps {
  children: React.ReactNode
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MenuPopover = React.forwardRef(({ children, ...props }: MenuPopoverProps, forwardedRef: any) => {
  return (
    <Portal>
      <Content
        ref={forwardedRef}
        className={styles.menuPopoverContent}
        style={{
          zIndex: theme.zIndices.popover
        }}
        align="start"
        side="bottom"
        sideOffset={10}
        {...props}
      >
        {children}
      </Content>
    </Portal>
  )
})
MenuPopover.displayName = 'MenuPopover'

export interface MenuContentProps extends BoxProps {
  fullWidthMobile?: boolean
  level?: 'sm' | 'md' | 'lg'
}

export const MenuContent = ({
  children,
  level = 'md',
  fullWidthMobile = false,
  sx,
  ...props
}: MenuContentProps): JSX.Element => (
  <Box
    data-name="MenuContent"
    sx={{
      borderRadius: '3px',
      boxShadow: 'menu',
      backgroundColor: 'var(--floating-element-background)',
      borderColor: 'var(--floating-element-border)',
      borderStyle: 'solid',
      borderWidth: '1px',
      width: '100%',
      maxWidth: ['100%', MENU_LIST_PROPS[level].max],
      minWidth: [fullWidthMobile ? '100vw' : '100%', MENU_LIST_PROPS[level].min],
      outline: 0,
      position: 'relative',
      ...sx
    }}
    {...props}
  >
    {children}
  </Box>
)

export const MenuSection = ({ children, sx, ...props }: BoxProps): JSX.Element => (
  <Box
    data-name="MenuSection"
    sx={{
      position: 'relative',
      borderBottom: '1px solid',
      borderColor: 'var(--floating-element-border)',
      p: 3,
      '&:last-of-type': {
        borderBottom: 0
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Box>
)

export const MenuContainer = styled(Box)`
  max-height: ${({ maxHeight }) => maxHeight || '50dvh'};
  overflow-y: auto;
  position: relative;
`

export const MenuWrapper = styled.div`
  position: relative;
`

export const MenuHeading = ({ children, sx, ...props }: TextProps): JSX.Element => (
  <Text
    data-name="MenuHeading"
    as="h3"
    variant="heading.xs"
    sx={{ marginBottom: 'var(--spacing-s)', ...sx }}
    {...props}
  >
    {children}
  </Text>
)

export const MenuAction = ({
  variant = 'tertiary',
  fontSize = 0,
  fontWeight = 'bold',
  p = '12px 12px',
  children,
  sx,
  ...props
}: ButtonProps): JSX.Element => (
  <Button
    variant={variant}
    fontSize={fontSize}
    fontWeight={fontWeight}
    p={p}
    sx={{
      border: '1px solid transparent',
      borderRadius: '3px',
      alignItems: 'center',
      display: 'inline-block',
      outline: 0,
      cursor: 'pointer',
      lineHeight: 1,
      textDecoration: 'none',
      textAlign: 'center',
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
)

export { MenuItem }
